import React, { useState } from 'react';
import { useCurrentSubscription } from 'src/store/subscription';
import { SubscriptionPaymentIntervalTypes, SubscriptionTypes } from 'src/dashboard/settings/billing';
import { DashboardSettingsBillingFeatureMapping } from 'src/dashboard/settings/billing/feature-mapping';
import { BillingToggle } from 'src/dashboard/settings/billing/subscription/billing-toggle';
import { usePostHog } from 'posthog-js/react';

import { apiSubscribeMonthly } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import getStripe from 'src/common/stripe';

const UpgradePaywall: React.FC = () => {
  const [activeInterval, setActiveInterval] = useState<SubscriptionPaymentIntervalTypes>('year');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const { currentOrganization } = useCurrentOrganization();
  const { setErrorNotification, setSuccessNotification } = useNotificationMessage();
  const posthog = usePostHog();

  const handleIntervalChange = (intervalType: SubscriptionPaymentIntervalTypes) => {
    setActiveInterval(intervalType);
  };

  const handleUpgrade = async (planType: SubscriptionTypes) => {
    try {
      setIsLoading(true);
      
      // Create Stripe checkout session for new subscription
      const response = await apiSubscribeMonthly(currentOrganization.id, {
        method: activeInterval,
        plan: planType
      });
      
      // Redirect to Stripe checkout
      const stripe = getStripe();
      if (stripe) {
        stripe.redirectToCheckout({ sessionId: response.session_id });
      } else {
        setErrorNotification('Stripe is not available. Please refresh the page and try again.');
      }
      
    } catch (error) {
      console.error('Failed to process upgrade:', error);
      setErrorNotification('Failed to process upgrade. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getPlanDisplayName = (planType: SubscriptionTypes) => {
    switch (planType) {
      case 'starter':
        return 'Start';
      case 'pro':
        return 'Grow';
      case 'enterprise':
        return 'Scale';
      default:
        return planType;
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 w-full p-6">
      <div className="w-full p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Your trial has expired</h1>
          <p className="text-gray-600">Choose a plan to continue using Journey and unlock all features</p>
        </div>

        <div className="mb-6">
          <BillingToggle
            checked={activeInterval === 'year'}
            onChange={handleIntervalChange}
            labelType='strong'
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Starter Plan */}
          <div className="border rounded-lg p-6 flex flex-col bg-white">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Start</h2>
              <p className="text-gray-600">
                {activeInterval === 'year' ? '$89/year per user' : '$9/month per user'}
              </p>
            </div>
            
            <ul className="mb-6 flex-grow">
              {DashboardSettingsBillingFeatureMapping.StartPlan.map((feature, index) => (
                <li key={index} className="flex items-start mb-2">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              type="button"
              className="disabled:opacity-50 btn-bedrock-primary w-full"
              onClick={() => handleUpgrade('starter')}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Upgrade to Start'}
            </button>
          </div>

          {/* Professional Plan */}
          <div className="border rounded-lg p-6 flex flex-col bg-blue-50 border-blue-300">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Grow</h2>
              <p className="text-gray-600">
                {activeInterval === 'year' ? '$289/year per user' : '$29/month per user'}
              </p>
              <span className="inline-block bg-blue-500 text-white text-xs px-2 py-1 rounded mt-2">MOST POPULAR</span>
            </div>
            
            <ul className="mb-6 flex-grow">
              {DashboardSettingsBillingFeatureMapping.GrowPlan.map((feature, index) => (
                <li key={index} className="flex items-start mb-2">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              type="button"
              className="disabled:opacity-50 btn-bedrock-primary w-full"
              onClick={() => handleUpgrade('pro')}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Upgrade to Grow'}
            </button>
          </div>

          {/* Growth Plan */}
          <div className="border rounded-lg p-6 flex flex-col bg-white">
            <div className="mb-4">
              <h2 className="text-xl font-bold">Scale</h2>
              <p className="text-gray-600">
                {activeInterval === 'year' ? '$589/year per user' : '$59/month per user'}
              </p>
            </div>
            
            <ul className="mb-6 flex-grow">
              {DashboardSettingsBillingFeatureMapping.ScalePlan.map((feature, index) => (
                <li key={index} className="flex items-start mb-2">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            
            <button 
              type="button"
              className="disabled:opacity-50 btn-bedrock-primary w-full"
              onClick={() => handleUpgrade('enterprise')}
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Upgrade to Scale'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradePaywall; 